/* eslint-disable max-len */
import './navbar.scss';

import axios from 'axios';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Button,
  Nav, Navbar,
} from 'react-bootstrap';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { BsPlusLg, BsXLg } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { splitStringOnUppercase } from '../../../../services/format';
import { selectIsAssetReadOnly } from '../../../../store/slices/auth-slice';
import { selectMaintenanceRequestDropdownObjects } from '../../../../store/slices/maintenance-requests-slice';
import { usePermissions } from '../../../hooks/usePermissions';
import { ordersPermissions } from '../../../pages/Orders/orders-datasource';
import ActiveLocationNavItem from '../../molecules/ActiveLocationNavItem/active-location-nav-item';

function RNNavbar(props) {
  const PER_PAGE = 50;
  const MIN_SEARCH_LENGTH = 4;
  const navigate = useNavigate();
  const customerTypes = ['Liquid Customer', 'Gas Customer', 'Packaged Customer'];
  const breadCrumbPaths = ['assets', 'admin', 'packaged-product', 'liquid-container', 'gas-container', 'chassis'];
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [assetApiFailed, setAssetApiFailed] = useState(false);
  const assetNavigateRoute = {
    0: 'gas-container', 1: 'liquid-container', 2: 'packaged-product', 3: 'chassis',
  };
  const searchRef = useRef(null);
  const latestQueryRef = useRef('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const isAssetReadOnly = useSelector(selectIsAssetReadOnly);
  const [ordersWriteAccess, ordersReadAccess] = usePermissions(ordersPermissions);
  // Read via ref inside the memoized handleSearch closure so the permission is
  // always current (roles load after mount; the closure would capture a stale value).
  const ordersReadAccessRef = useRef(ordersReadAccess);
  ordersReadAccessRef.current = ordersReadAccess;
  const maintenanceDropdownValues = useSelector(selectMaintenanceRequestDropdownObjects);
  const initialSearchedAsset = {
    serialNumber: '',
    isActive: '',
    assetType: '',
    id: '',
  };
  const [searchedAsset, setSearchedAsset] = useState(initialSearchedAsset);
  let CACHE = {};

  let assetSearchPlaceholder;
  if (assetApiFailed) {
    assetSearchPlaceholder = 'Error loading results';
  } else if (isLoading) {
    assetSearchPlaceholder = 'Loading...';
  } else {
    assetSearchPlaceholder = 'Search...';
  }
  const getCustomerType = (record) => {
    if (record.assetType === 'Liquid Customer') {
      return 'liquid';
    }
    if (record.assetType === 'Gas Customer') {
      return 'gas';
    }
    return 'dewars';
  };
  const getEntityType = (record) => {
    if (record.assetType === 'Order') return 'order';
    if (customerTypes.includes(record.assetType)) return 'customer';
    return 'asset';
  };
  const getLink = (option) => {
    const theType = getEntityType(option);
    if (theType === 'order') {
      return `/orders/edit/${option.id}`;
    }
    if (theType === 'asset') {
      let routingId = '';
      if (option.assetType === 2) {
        routingId = option.id;
      } else {
        routingId = option.serialNumber;
      }
      return `/assets/${assetNavigateRoute[option.assetType]}/${routingId}`;
    }

    return `/customers/edit/${getCustomerType(option)}/${option.serialNumber}`;
  };
  const getLinkText = (option) => {
    const theType = getEntityType(option);
    if (theType === 'order') {
      return option.orderType ? `${option.orderType} Order` : 'Order';
    }
    if (theType === 'asset') {
      return option && option.assetType + 1 in maintenanceDropdownValues.assetTypes ? splitStringOnUppercase(maintenanceDropdownValues.assetTypes[option.assetType + 1].name) : 'N/A';
    }
    return option.assetType;
  };
  const handleInputChange = (q) => {
    setQuery(q);
  };

  function makeAndHandleAssetRequest(searchValue, page = 1) {
    return axios.get(`/AssetManagement/Assets?searchValue=${searchValue}`)
      .then((resp) => resp.data);
  }
  function makeAndHandleCustomerRequest(searchValue, page = 1) {
    if (!searchValue?.trim()) return Promise.resolve([]);
    return axios.get(`/Customer/SearchCustomer/${searchValue}`)
      .then((resp) => resp.data);
  }
  function makeAndHandleOrderRequest(searchValue) {
    if (!ordersReadAccessRef.current || !searchValue?.trim()) return Promise.resolve([]);
    return axios.get(`/Orders/SearchOrders/${encodeURIComponent(searchValue)}`)
      .then((resp) => (resp.data || []).map((o) => ({
        assetType: 'Order',
        serialNumber: o.orderNumber ? String(o.orderNumber) : `Id ${o.id}`,
        subHeading: o.customerName,
        orderType: o.orderType,
        id: o.id,
      })))
      .catch(() => []);
  }

  const makeAndHandleRequest = async (searchValue, page = 1) => {
    // "asset:" prefix scopes the unified search to assets only.
    const assetMatch = /^\s*asset:\s*(.*)$/i.exec(searchValue);
    if (assetMatch) {
      const term = assetMatch[1].trim();
      return term ? makeAndHandleAssetRequest(term, page) : [];
    }

    // "order:" prefix scopes the unified search to orders only.
    const orderMatch = /^\s*order:\s*(.*)$/i.exec(searchValue);
    if (orderMatch) {
      return makeAndHandleOrderRequest(orderMatch[1].trim());
    }

    // "customer:" prefix scopes the unified search to customers only.
    const customerMatch = /^\s*customer:\s*(.*)$/i.exec(searchValue);
    if (customerMatch) {
      return makeAndHandleCustomerRequest(customerMatch[1].trim(), page);
    }

    const [Promise1Result, Promise2Result, Promise3Result] = await Promise.allSettled([
      makeAndHandleAssetRequest(searchValue, page),
      makeAndHandleCustomerRequest(searchValue, page),
      makeAndHandleOrderRequest(searchValue),
    ]);
    return [
      ...(Promise1Result.value || []),
      ...(Promise2Result.value || []),
      ...(Promise3Result.value || []),
    ];
  };

  useEffect(() => {
    searchRef.current?.clear();
  }, [location.pathname]);

  // Combined results are grouped by category (customers before orders) before
  // falling back to alphabetical order within each group.
  const searchResultRank = { customer: 0, asset: 1, order: 2 };
  const compareSearchResults = (a, b) => {
    const rankDiff = searchResultRank[getEntityType(a)] - searchResultRank[getEntityType(b)];
    if (rankDiff !== 0) return rankDiff;
    return a.serialNumber.trim().localeCompare(b.serialNumber.trim());
  };

  const handleSearch = useCallback((q) => {
    // Track the most recent query so out-of-order responses can be discarded.
    // Without this, a slower earlier request (e.g. a customer search fired for an
    // intermediate "order..." keystroke) can resolve after a newer request and
    // overwrite the correct results.
    latestQueryRef.current = q;

    if (CACHE[q]) {
      setOptions(CACHE[q].assetSearch.sort(compareSearchResults));
      return;
    }

    setIsLoading(true);
    makeAndHandleRequest(q, '').then((resp) => {
      CACHE[q] = { assetSearch: resp };
      if (latestQueryRef.current !== q) return;
      setIsLoading(false);
      setOptions(resp.sort(compareSearchResults));
      setAssetApiFailed(false);
    }).catch((err) => {
      if (latestQueryRef.current !== q) return;
      setIsLoading(false);
      setAssetApiFailed(true);
    });
  }, [searchedAsset]);

  const searchPrefixes = [
    { prefix: 'asset:', description: 'Search assets only' },
    { prefix: 'customer:', description: 'Search customers only' },
    ...(ordersReadAccess ? [{ prefix: 'order:', description: 'Search orders only' }] : []),
  ];
  const showPrefixHelper = isSearchFocused
    && query.trim().length < MIN_SEARCH_LENGTH
    && !/^\s*\w+:/.test(query);

  const applySearchPrefix = (prefix) => {
    const input = searchRef.current?.getInput();
    if (!input) return;
    // Use the native setter + input event so the typeahead sees a real user edit
    // (fires onInputChange/onSearch); it exposes no public API to set its text.
    const setValue = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    setValue.call(input, prefix);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  };

  const resetSearch = () => {
    if (query in CACHE) {
      CACHE = {};
    }
    latestQueryRef.current = '';
    setSearchedAsset(initialSearchedAsset);
    setQuery('');
    setOptions([]);
    setAssetApiFailed(false);
    setIsLoading(false);
  };

  return (
    <Navbar className="main-header navbar navbar-expand navbar-primary navbar-dark custom-navbar">
      <Nav>
        <Nav.Item className="mx-3"><Button onClick={props.toggleClick} variant="outline-none"><span className="navbar-toggler-icon" /></Button></Nav.Item>
      </Nav>
      {(!isAssetReadOnly || ordersReadAccess || ordersWriteAccess) && (
        <div className={`global-search-wrapper w-25 position-relative${ordersWriteAccess ? ' has-create' : ''}`}>
          {(!isAssetReadOnly || ordersReadAccess) && (
            <AsyncTypeahead
              clearButton
              ref={searchRef}
              className="w-100 global-search-nav-input"
              id="async-pagination-example"
              isLoading={isLoading}
              filterBy={() => true}
              labelKey="serialNumber"
              maxResults={PER_PAGE - 1}
              minLength={MIN_SEARCH_LENGTH}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              onInputChange={handleInputChange}
              onSearch={handleSearch}
              options={options}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  searchRef.current?.clear();
                  setQuery('');
                }
              }}
              paginate
              delay={500}
              useCache={false}
              placeholder={assetSearchPlaceholder}
              onChange={(e) => {
                try {
                  if (e[0]) {
                    setSearchedAsset(e[0]);
                  }
                } catch (err) {
                  console.error(err);
                }
              }}
              renderMenuItemChildren={(option) => {
                let routingId = '';
                if (option.assetType === 2) {
                  routingId = option.id;
                } else {
                  routingId = option.serialNumber;
                }
                return (
                  <NavLink
                    onClick={() => resetSearch()}
                    to={getLink(option)}
                  >
                    <div key={option.serialNumber}>
                      <span className="d-inline-flex w-100 h-100 justify-content-between">
                        {option.serialNumber}
                        {option.subHeading ? ` - ${option.subHeading}` : ''}
                      </span>
                      <div className="d-flex flex-row justify-content-between">
                        <div className="flex-md-column">
                          <small>
                            {getLinkText(option)}
                          </small>
                        </div>
                      </div>
                    </div>
                  </NavLink>
                );
              }}
            >
              {({ onClear }) => (
                <div className="rbt-aux">
                  {(!isLoading && query !== '') && <BsXLg onClick={onClear} />}
                </div>
              )}
            </AsyncTypeahead>
          )}

          {showPrefixHelper && (
            <div className="global-search-prefix-helper dropdown-menu show" role="listbox">
              <h6 className="dropdown-header">Narrow your search with a prefix</h6>
              {searchPrefixes.map(({ prefix, description }) => (
                <button
                  key={prefix}
                  type="button"
                  className="dropdown-item"
                  // Prevent the input from blurring before the click is handled.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applySearchPrefix(prefix)}
                >
                  <code>{prefix}</code>
                  <small className="text-muted ml-2">{description}</small>
                </button>
              ))}
            </div>
          )}

          {ordersWriteAccess && (
            <div className="global-create-dropdown">
              <Button
                id="global-create-button"
                className="global-create-toggle"
                title="Create new order"
                onClick={() => navigate('/orders/create')}
              >
                <BsPlusLg />
              </Button>
            </div>
          )}
        </div>
      )}

      <Nav className="ml-auto">
        <ActiveLocationNavItem />
      </Nav>
    </Navbar>
  );
}
// eslint-disable-next-line max-len, no-lone-blocks
{ /* <a class="nav-link dropdown-toggle" href="#" id="navbarDropdown2" role="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
        Help
      </a> */ }
// eslint-disable-next-line no-lone-blocks
{ /* <div class="dropdown-menu" aria-labelledby="navbarDropdown2">
        <a class="dropdown-item" href="#">FAQ</a>
        <a class="dropdown-item" href="#">Support</a>
        <div class="dropdown-divider"></div>
        <a class="dropdown-item" href="#">Contact</a>
      </div>
    </li> */ }

export default RNNavbar;
